import Layout from '../../components/base/Layout';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

export default function Onderhoudstechniek() {
  const { t } = useTranslation();
  const [expandedCase, setExpandedCase] = useState<number | null>(null);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-24 h-24 flex items-center justify-center bg-gradient-to-br from-blue-600 to-blue-800 rounded-3xl mx-auto mb-6 shadow-lg">
            <i className="ri-tools-line text-5xl text-white"></i>
          </div>
          <h1 className="text-5xl font-bold text-gray-800 mb-4">
            {t('maint_title')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t('maint_subtitle')}
          </p>
        </div>

        {/* 1. Wat is onderhoudstechniek? */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 shadow-lg border border-blue-200">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-xl shadow-md">
              <i className="ri-question-line text-3xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-blue-900">{t('maint_what_title')}</h2>
          </div>
          
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            {t('maint_what_desc')}
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {[
              { icon: 'ri-tools-fill', title: t('maint_focus1'), desc: t('maint_focus1_desc'), color: 'blue' },
              { icon: 'ri-shield-check-line', title: t('maint_focus2'), desc: t('maint_focus2_desc'), color: 'green' },
              { icon: 'ri-line-chart-line', title: t('maint_focus3'), desc: t('maint_focus3_desc'), color: 'purple' },
              { icon: 'ri-shield-user-line', title: t('maint_focus4'), desc: t('maint_focus4_desc'), color: 'orange' }
            ].map((item, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className={`w-14 h-14 flex items-center justify-center bg-${item.color}-100 rounded-lg flex-shrink-0`}>
                    <i className={`${item.icon} text-2xl text-${item.color}-600`}></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-gray-800">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-blue-600 text-white p-6 rounded-xl">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-500 rounded-lg flex-shrink-0">
                <i className="ri-information-line text-2xl"></i>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-2">{t('maint_logistics_title')}</h4>
                <p className="leading-relaxed">{t('maint_logistics_desc')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* 2. Soorten onderhoud */}
        <div className="bg-white rounded-2xl p-8 shadow-lg">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-16 h-16 flex items-center justify-center bg-indigo-600 rounded-xl shadow-md">
              <i className="ri-list-check text-3xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-800">{t('maint_types_title')}</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Preventief onderhoud */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border-2 border-green-300">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-14 h-14 flex items-center justify-center bg-green-600 rounded-xl">
                  <i className="ri-calendar-check-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-green-900">{t('maint_preventive')}</h3>
              </div>
              <p className="text-gray-700 mb-4">{t('maint_preventive_desc')}</p>
              <div className="space-y-3">
                {[
                  t('maint_prev1'),
                  t('maint_prev2'),
                  t('maint_prev3'),
                  t('maint_prev4')
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3 bg-white p-3 rounded-lg">
                    <div className="w-6 h-6 flex items-center justify-center bg-green-100 rounded-full">
                      <i className="ri-check-line text-green-600 text-sm"></i>
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Correctief onderhoud */}
            <div className="bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-xl border-2 border-red-300">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-14 h-14 flex items-center justify-center bg-red-600 rounded-xl">
                  <i className="ri-tools-fill text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-red-900">{t('maint_corrective')}</h3>
              </div>
              <p className="text-gray-700 mb-4">{t('maint_corrective_desc')}</p>
              <div className="space-y-3">
                {[
                  t('maint_corr1'),
                  t('maint_corr2'),
                  t('maint_corr3')
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3 bg-white p-3 rounded-lg">
                    <div className="w-6 h-6 flex items-center justify-center bg-red-100 rounded-full">
                      <i className="ri-alert-line text-red-600 text-sm"></i>
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Predictief onderhoud */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border-2 border-purple-300">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-14 h-14 flex items-center justify-center bg-purple-600 rounded-xl">
                  <i className="ri-line-chart-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-purple-900">{t('maint_predictive')}</h3>
              </div>
              <p className="text-gray-700 mb-4">{t('maint_predictive_desc')}</p>
              <div className="space-y-3">
                {[
                  t('maint_pred1'),
                  t('maint_pred2'),
                  t('maint_pred3'),
                  t('maint_pred4')
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3 bg-white p-3 rounded-lg">
                    <div className="w-6 h-6 flex items-center justify-center bg-purple-100 rounded-full">
                      <i className="ri-radar-line text-purple-600 text-sm"></i>
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Periodiek onderhoud */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border-2 border-blue-300">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-14 h-14 flex items-center justify-center bg-blue-600 rounded-xl">
                  <i className="ri-calendar-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-blue-900">{t('maint_periodic')}</h3>
              </div>
              <p className="text-gray-700 mb-4">{t('maint_periodic_desc')}</p>
              <div className="space-y-3">
                {[
                  t('maint_per1'),
                  t('maint_per2'),
                  t('maint_per3')
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3 bg-white p-3 rounded-lg">
                    <div className="w-6 h-6 flex items-center justify-center bg-blue-100 rounded-full">
                      <i className="ri-time-line text-blue-600 text-sm"></i>
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 3. Belangrijke onderdelen */}
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 shadow-lg">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-16 h-16 flex items-center justify-center bg-gray-700 rounded-xl shadow-md">
              <i className="ri-settings-3-line text-3xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-800">{t('maint_components_title')}</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[
              { icon: 'ri-flashlight-line', title: t('maint_comp1'), desc: t('maint_comp1_desc'), color: 'blue' },
              { icon: 'ri-record-circle-line', title: t('maint_comp2'), desc: t('maint_comp2_desc'), color: 'green' },
              { icon: 'ri-checkbox-blank-circle-line', title: t('maint_comp3'), desc: t('maint_comp3_desc'), color: 'purple' },
              { icon: 'ri-arrow-right-line', title: t('maint_comp4'), desc: t('maint_comp4_desc'), color: 'orange' },
              { icon: 'ri-speed-line', title: t('maint_comp5'), desc: t('maint_comp5_desc'), color: 'red' },
              { icon: 'ri-sensor-line', title: t('maint_comp6'), desc: t('maint_comp6_desc'), color: 'indigo' },
              { icon: 'ri-links-line', title: t('maint_comp7'), desc: t('maint_comp7_desc'), color: 'yellow' },
              { icon: 'ri-plug-line', title: t('maint_comp8'), desc: t('maint_comp8_desc'), color: 'teal' }
            ].map((item, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all">
                <div className={`w-14 h-14 flex items-center justify-center bg-${item.color}-100 rounded-xl mb-4`}>
                  <i className={`${item.icon} text-2xl text-${item.color}-600`}></i>
                </div>
                <h3 className="text-lg font-semibold mb-2 text-gray-800">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <img 
              src="https://public.readdy.ai/ai/img_res/4c14082d056979ee8c5c1eda0d6c39c8.jpg"
              alt={t('maint_components_title')}
              className="w-full rounded-xl shadow-md object-cover object-top"
            />
            <img 
              src="https://readdy.ai/api/search-image?query=Industrial%20sensors%20inverters%20and%20electrical%20control%20components%2C%20automation%20equipment%2C%20professional%20technical%20installation%2C%20modern%20industrial%20technology%2C%20clean%20organized%20setup&width=600&height=400&seq=comp2&orientation=landscape"
              alt={t('maint_components_title')}
              className="w-full rounded-xl shadow-md object-cover object-top"
            />
          </div>
        </div>

        {/* 4. Diagnose en storingsanalyse */}
        <div className="bg-white rounded-2xl p-8 shadow-lg">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-16 h-16 flex items-center justify-center bg-cyan-600 rounded-xl shadow-md">
              <i className="ri-search-line text-3xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-800">{t('maint_diagnosis_title')}</h2>
          </div>

          <p className="text-lg text-gray-700 mb-6">{t('maint_diagnosis_desc')}</p>

          <div className="space-y-4">
            {[
              { step: 1, icon: 'ri-code-line', title: t('maint_diag1'), desc: t('maint_diag1_desc'), color: 'blue' },
              { step: 2, icon: 'ri-eye-line', title: t('maint_diag2'), desc: t('maint_diag2_desc'), color: 'green' },
              { step: 3, icon: 'ri-dashboard-line', title: t('maint_diag3'), desc: t('maint_diag3_desc'), color: 'purple' },
              { step: 4, icon: 'ri-sensor-line', title: t('maint_diag4'), desc: t('maint_diag4_desc'), color: 'orange' },
              { step: 5, icon: 'ri-sound-module-line', title: t('maint_diag5'), desc: t('maint_diag5_desc'), color: 'red' },
              { step: 6, icon: 'ri-tools-line', title: t('maint_diag6'), desc: t('maint_diag6_desc'), color: 'indigo' },
              { step: 7, icon: 'ri-file-text-line', title: t('maint_diag7'), desc: t('maint_diag7_desc'), color: 'yellow' },
              { step: 8, icon: 'ri-shield-check-line', title: t('maint_diag8'), desc: t('maint_diag8_desc'), color: 'teal' }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="flex items-start space-x-4">
                  <div className={`flex-shrink-0 w-16 h-16 bg-${item.color}-100 rounded-xl flex items-center justify-center`}>
                    <div className={`w-10 h-10 bg-${item.color}-600 rounded-lg flex items-center justify-center text-white font-bold text-lg`}>
                      {item.step}
                    </div>
                  </div>
                  <div className="flex-1 bg-gray-50 p-5 rounded-xl">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className={`w-8 h-8 flex items-center justify-center bg-${item.color}-100 rounded-lg`}>
                        <i className={`${item.icon} text-${item.color}-600`}></i>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800">{item.title}</h3>
                    </div>
                    <p className="text-gray-600 text-sm ml-11">{item.desc}</p>
                  </div>
                </div>
                {index < 7 && (
                  <div className="absolute left-8 top-16 w-0.5 h-4 bg-gray-300"></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 5. Onderhoudsprocedures */}
        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-2xl p-8 shadow-lg border border-indigo-200">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-16 h-16 flex items-center justify-center bg-indigo-600 rounded-xl shadow-md">
              <i className="ri-file-list-3-line text-3xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-indigo-900">{t('maint_procedures_title')}</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: 'ri-lock-line', title: t('maint_proc1'), desc: t('maint_proc1_desc'), color: 'red' },
              { icon: 'ri-flashlight-line', title: t('maint_proc2'), desc: t('maint_proc2_desc'), color: 'orange' },
              { icon: 'ri-drop-line', title: t('maint_proc3'), desc: t('maint_proc3_desc'), color: 'blue' },
              { icon: 'ri-arrow-left-right-line', title: t('maint_proc4'), desc: t('maint_proc4_desc'), color: 'green' },
              { icon: 'ri-settings-4-line', title: t('maint_proc5'), desc: t('maint_proc5_desc'), color: 'purple' },
              { icon: 'ri-links-line', title: t('maint_proc6'), desc: t('maint_proc6_desc'), color: 'indigo' },
              { icon: 'ri-brush-line', title: t('maint_proc7'), desc: t('maint_proc7_desc'), color: 'teal' },
              { icon: 'ri-checkbox-circle-line', title: t('maint_proc8'), desc: t('maint_proc8_desc'), color: 'yellow' }
            ].map((item, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 flex items-center justify-center bg-${item.color}-100 rounded-lg flex-shrink-0`}>
                    <i className={`${item.icon} text-xl text-${item.color}-600`}></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-gray-800">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6">
            <img 
              src="https://readdy.ai/api/search-image?query=Industrial%20maintenance%20procedures%20technician%20performing%20preventive%20maintenance%20on%20conveyor%20system%2C%20professional%20maintenance%20work%2C%20safety%20equipment%2C%20organized%20technical%20procedures%2C%20modern%20industrial%20facility&width=1200&height=400&seq=proc1&orientation=landscape"
              alt={t('maint_procedures_title')}
              className="w-full rounded-xl shadow-md object-cover object-top"
            />
          </div>
        </div>

        {/* 6. Veiligheid bij onderhoud (VCA) */}
        <div className="bg-red-50 rounded-2xl p-8 shadow-lg border-2 border-red-300">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-16 h-16 flex items-center justify-center bg-red-600 rounded-xl shadow-md">
              <i className="ri-shield-check-line text-3xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-red-900">{t('maint_safety_title')}</h2>
          </div>

          <div className="bg-red-100 border-l-4 border-red-600 p-6 rounded-lg mb-6">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-200 rounded-lg flex-shrink-0">
                <i className="ri-alert-line text-2xl text-red-700"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-red-900 mb-2">{t('maint_safety_warning')}</h3>
                <p className="text-red-800 leading-relaxed">{t('maint_safety_warning_desc')}</p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {[
              { icon: 'ri-shut-down-line', title: t('maint_safe1'), desc: t('maint_safe1_desc') },
              { icon: 'ri-lock-line', title: t('maint_safe2'), desc: t('maint_safe2_desc') },
              { icon: 'ri-shield-user-line', title: t('maint_safe3'), desc: t('maint_safe3_desc') },
              { icon: 'ri-error-warning-line', title: t('maint_safe4'), desc: t('maint_safe4_desc') },
              { icon: 'ri-tools-line', title: t('maint_safe5'), desc: t('maint_safe5_desc') },
              { icon: 'ri-fire-line', title: t('maint_safe6'), desc: t('maint_safe6_desc') },
              { icon: 'ri-shield-line', title: t('maint_safe7'), desc: t('maint_safe7_desc') },
              { icon: 'ri-close-circle-line', title: t('maint_safe8'), desc: t('maint_safe8_desc') }
            ].map((item, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md border-l-4 border-red-500">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-xl text-red-600`}></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-gray-800">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div>
            <img 
              src="https://readdy.ai/api/search-image?query=Industrial%20safety%20VCA%20lockout%20tagout%20procedures%2C%20technician%20wearing%20full%20PPE%20safety%20equipment%20helmet%20gloves%2C%20professional%20safety%20measures%2C%20industrial%20maintenance%20safety%2C%20organized%20safe%20workplace&width=1200&height=400&seq=safety3&orientation=landscape"
              alt={t('maint_safety_title')}
              className="w-full rounded-xl shadow-md object-cover object-top"
            />
          </div>
        </div>

        {/* 7. Veelvoorkomende problemen en oplossingen */}
        <div className="bg-white rounded-2xl p-8 shadow-lg">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-16 h-16 flex items-center justify-center bg-yellow-600 rounded-xl shadow-md">
              <i className="ri-error-warning-line text-3xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-800">{t('maint_problems_title')}</h2>
          </div>

          <div className="space-y-4">
            {[
              { problem: t('maint_prob1'), solution: t('maint_prob1_sol'), icon: 'ri-record-circle-line', color: 'red' },
              { problem: t('maint_prob2'), solution: t('maint_prob2_sol'), icon: 'ri-arrow-left-right-line', color: 'orange' },
              { problem: t('maint_prob3'), solution: t('maint_prob3_sol'), icon: 'ri-sensor-line', color: 'yellow' },
              { problem: t('maint_prob4'), solution: t('maint_prob4_sol'), icon: 'ri-plug-line', color: 'purple' },
              { problem: t('maint_prob5'), solution: t('maint_prob5_sol'), icon: 'ri-temp-hot-line', color: 'red' },
              { problem: t('maint_prob6'), solution: t('maint_prob6_sol'), icon: 'ri-link-unlink-m', color: 'blue' },
              { problem: t('maint_prob7'), solution: t('maint_prob7_sol'), icon: 'ri-contrast-drop-line', color: 'green' },
              { problem: t('maint_prob8'), solution: t('maint_prob8_sol'), icon: 'ri-checkbox-blank-circle-line', color: 'indigo' },
              { problem: t('maint_prob9'), solution: t('maint_prob9_sol'), icon: 'ri-speed-line', color: 'teal' }
            ].map((item, index) => (
              <div key={index} className={`bg-${item.color}-50 p-6 rounded-xl border-l-4 border-${item.color}-500 hover:shadow-md transition-shadow`}>
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 flex items-center justify-center bg-${item.color}-100 rounded-lg flex-shrink-0`}>
                    <i className={`${item.icon} text-xl text-${item.color}-600`}></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-3">
                      <span className={`text-xs font-bold text-${item.color}-700 bg-${item.color}-200 px-3 py-1 rounded-full`}>
                        {t('maint_problem')}
                      </span>
                      <h3 className="text-lg font-semibold text-gray-800">{item.problem}</h3>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="text-xs font-bold text-green-700 bg-green-200 px-3 py-1 rounded-full">
                        {t('maint_solution')}
                      </span>
                      <p className="text-gray-700 flex-1">{item.solution}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 8. Praktijkvoorbeelden */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 shadow-lg border border-green-200">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-16 h-16 flex items-center justify-center bg-green-600 rounded-xl shadow-md">
              <i className="ri-lightbulb-line text-3xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-green-900">{t('maint_cases_title')}</h2>
          </div>

          <p className="text-lg text-gray-700 mb-6">{t('maint_cases_desc')}</p>

          <div className="space-y-4">
            {[
              { 
                title: t('maint_case1_title'), 
                problem: t('maint_case1_problem'),
                cause: t('maint_case1_cause'),
                solution: t('maint_case1_solution'),
                icon: 'ri-flashlight-line',
                color: 'blue'
              },
              { 
                title: t('maint_case2_title'), 
                problem: t('maint_case2_problem'),
                cause: t('maint_case2_cause'),
                solution: t('maint_case2_solution'),
                icon: 'ri-arrow-left-right-line',
                color: 'orange'
              },
              { 
                title: t('maint_case3_title'), 
                problem: t('maint_case3_problem'),
                cause: t('maint_case3_cause'),
                solution: t('maint_case3_solution'),
                icon: 'ri-sensor-line',
                color: 'purple'
              },
              { 
                title: t('maint_case4_title'), 
                problem: t('maint_case4_problem'),
                cause: t('maint_case4_cause'),
                solution: t('maint_case4_solution'),
                icon: 'ri-cpu-line',
                color: 'red'
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden">
                <button
                  onClick={() => setExpandedCase(expandedCase === index ? null : index)}
                  className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className="flex items-center space-x-4">
                    <div className={`w-14 h-14 flex items-center justify-center bg-${item.color}-100 rounded-xl`}>
                      <i className={`${item.icon} text-2xl text-${item.color}-600`}></i>
                    </div>
                    <div className="text-left">
                      <h3 className="text-xl font-semibold text-gray-800">{item.title}</h3>
                      <p className="text-gray-600 text-sm">{t('maint_click_details')}</p>
                    </div>
                  </div>
                  <div className={`w-8 h-8 flex items-center justify-center bg-${item.color}-100 rounded-lg transition-transform ${expandedCase === index ? 'rotate-180' : ''}`}>
                    <i className={`ri-arrow-down-s-line text-${item.color}-600`}></i>
                  </div>
                </button>
                
                {expandedCase === index && (
                  <div className="px-6 pb-6 space-y-4 border-t border-gray-200 pt-4">
                    <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-500">
                      <h4 className="font-semibold text-red-900 mb-2">{t('maint_problem')}:</h4>
                      <p className="text-gray-700">{item.problem}</p>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
                      <h4 className="font-semibold text-yellow-900 mb-2">{t('maint_cause')}:</h4>
                      <p className="text-gray-700">{item.cause}</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                      <h4 className="font-semibold text-green-900 mb-2">{t('maint_solution')}:</h4>
                      <p className="text-gray-700">{item.solution}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 9. Samenvatting */}
        <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white rounded-2xl p-8 shadow-lg">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-16 h-16 flex items-center justify-center bg-blue-500 rounded-xl shadow-md">
              <i className="ri-file-list-3-line text-3xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold">{t('maint_summary_title')}</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {[
              { icon: 'ri-question-line', text: t('maint_sum1') },
              { icon: 'ri-list-check', text: t('maint_sum2') },
              { icon: 'ri-search-line', text: t('maint_sum3') },
              { icon: 'ri-shield-check-line', text: t('maint_sum4') },
              { icon: 'ri-tools-line', text: t('maint_sum5') },
              { icon: 'ri-team-line', text: t('maint_sum6') }
            ].map((item, index) => (
              <div key={index} className="flex items-start space-x-4 bg-blue-500 p-5 rounded-xl">
                <div className="w-10 h-10 flex items-center justify-center bg-blue-400 rounded-lg flex-shrink-0">
                  <i className={`${item.icon} text-xl text-white`}></i>
                </div>
                <p className="text-blue-50 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>

          <div className="bg-blue-500 p-6 rounded-xl text-center">
            <p className="text-xl font-semibold leading-relaxed">
              {t('maint_final_message')}
            </p>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link 
            to="/elektrisch-schakelen"
            className="flex items-center text-blue-600 hover:text-blue-700 cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center mr-2">
              <i className="ri-arrow-left-line"></i>
            </div>
            {t('nav_electrical')}
          </Link>
          <Link 
            to="/"
            className="flex items-center bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t('nav_home')}
            <div className="w-5 h-5 flex items-center justify-center ml-2">
              <i className="ri-home-line"></i>
            </div>
          </Link>
        </div>
      </div>
    </Layout>
  );
}
